import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'

import TextInput from '../../../TextInput/TextInput'
import FieldLabel from '../../../FieldLabel/FieldLabel'
import Help from '../../../Help/Help'
import Paragraph from '../../../Typography/Paragraph/Paragraph'
import { getContentTypeFieldDataTestId, CONTENT_TYPE_FIELD_ACTION_IDS } from '../../helper'
import {
  getTaxonomyUidOptionsFromSchema,
  getRootTextFieldsForUrlPattern,
  getUrlPatternFieldValueFromOptions,
  schemaHasTaxonomyField,
  applyUrlPatternSuggestion,
  getUrlPatternSuggestionFilterQuery
} from './utils/urlManagementV2Utils'
import {
  buildVisibleSchemaFieldMenuRows,
  buildVisibleSchemaTaxonomyMenuRows,
  getVisibleUrlPatternSyntaxMenuRows,
  getUrlPatternFieldMenuRowTestId,
  getUrlPatternSyntaxMenuRowTestId,
  getUrlPatternTaxonomyMenuRowTestId,
  URL_PATTERN_FIELD_SUBMENU_INFO
} from './utils/urlPatternDropdownMenuModel'
import UrlPatternMenuRow from './UrlPatternMenuRow'

import './UrlPatternTextDropdown.css'
import Icon from '../../../Icon2/Icon'
import Info from '../../../Info/Info'

const URL_PATTERN_HINT_TEXT =
  'Select a variable to insert (such as month, day, or year).'

/** Right-hand label for the Figma “custom text” fallback row (typed fragment that is not a known token). */
const URL_PATTERN_SYNTAX_CUSTOM_TEXT_SUBTITLE = 'custom text'
const URL_PATTERN_PRESET_OVERRIDE_TOOLTIP =
  'Selecting this preset replaces the current URL pattern with the selected preset pattern.'

type MenuView = 'syntax' | 'fields' | 'taxonomy'

export interface UrlPatternTextDropdownProps {
  uniqueKey: string
  schema: any
  options: any
  setOptions: (options: any, actionFlag?: string) => void
  onNotifyChange: () => void
  disabled: boolean
  contentTypeSchemaTree?: any[]
  t?: (key: string) => string
}

const UrlPatternTextDropdown = ({
  uniqueKey,
  schema,
  options,
  setOptions,
  onNotifyChange,
  disabled,
  contentTypeSchemaTree,
  t = (key: string) => ''
}: UrlPatternTextDropdownProps) => {
  const [patternValue, setPatternValue] = useState(() => getUrlPatternFieldValueFromOptions(options))
  const [menuOpen, setMenuOpen] = useState(false)
  const [menuView, setMenuView] = useState<MenuView>('syntax')
  /** When false, suggestions ignore `patternValue` and show the full list (open from textbox click). Typing sets true. */
  const [suggestionFilterActive, setSuggestionFilterActive] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  /** Open syntax / field / taxonomy panel — clicks here keep the menu open while closing on outside mousedown. */
  const menuRef = useRef<HTMLDivElement | null>(null)
  const suppressFocusOpenRef = useRef(false)
  const hasPlacedInitialCaretRef = useRef(false)

  const rootTextFields = useMemo(() => getRootTextFieldsForUrlPattern(contentTypeSchemaTree ?? []), [contentTypeSchemaTree])
  const taxonomyUidOptions = useMemo(() => getTaxonomyUidOptionsFromSchema(contentTypeSchemaTree ?? []), [contentTypeSchemaTree])
  const hasTaxonomyField = useMemo(() => schemaHasTaxonomyField(contentTypeSchemaTree ?? []), [contentTypeSchemaTree])

  const filterQ = useMemo(
    () => getUrlPatternSuggestionFilterQuery(patternValue, suggestionFilterActive),
    [patternValue, suggestionFilterActive]
  )

  const visibleSyntaxMenuRows = useMemo(
    () =>
      getVisibleUrlPatternSyntaxMenuRows({
        filterQuery: filterQ,
        hasTaxonomyField,
        rootTextFields,
        taxonomyUidOptions
      }),
    [filterQ, hasTaxonomyField, rootTextFields, taxonomyUidOptions]
  )

  const visibleFieldSubmenuRows = useMemo(
    () => buildVisibleSchemaFieldMenuRows(rootTextFields, filterQ),
    [rootTextFields, filterQ]
  )

  const visibleTaxonomySubmenuRows = useMemo(
    () => buildVisibleSchemaTaxonomyMenuRows(taxonomyUidOptions, filterQ),
    [taxonomyUidOptions, filterQ]
  )

  const hasAnySyntaxSuggestion = visibleSyntaxMenuRows.length > 0

  /** Non-empty tail filter while typing → show “custom text” row (alone or after matches). */
  const shouldShowSyntaxCustomTextRow =
    suggestionFilterActive && menuView === 'syntax' && filterQ.trim().length > 0

  const showSyntaxDropdownList = hasAnySyntaxSuggestion || shouldShowSyntaxCustomTextRow

  const commitPattern = useCallback(
    (value: string) => {
      // Keep empty pattern as '' (legacy custom URL behaviour). `false` is only for preset-off via the toggle in Url.tsx.
      options.url_pattern = value
      setOptions(options)
      onNotifyChange()
    },
    [options, setOptions, onNotifyChange]
  )

  const closeMenu = useCallback(() => setMenuOpen(false), [])

  const patternFromOptions = useMemo(() => getUrlPatternFieldValueFromOptions(options), [options?.url_pattern])

  useEffect(() => {
    setPatternValue(patternFromOptions)
  }, [patternFromOptions])

  useEffect(() => {
    if (!menuOpen) return
    const isInside = (node: Node | null, root: Node | null): boolean =>
      Boolean(root && node && (root === node || root.contains(node)))

    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (isInside(t, inputRef.current)) return
      if (isInside(t, menuRef.current)) return
      closeMenu()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        closeMenu()
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen, closeMenu])

  useEffect(() => {
    if (!menuOpen) setMenuView('syntax')
  }, [menuOpen])

  useEffect(() => {
    if (!menuOpen) {
      setSuggestionFilterActive(false)
    }
  }, [menuOpen])

  const focusInputWithCaret = useCallback((caret: number, valueLen: number) => {
    const el = inputRef.current
    requestAnimationFrame(() => {
      if (el) {
        el.focus()
        const c = Math.min(Math.max(0, caret), valueLen)
        el.setSelectionRange(c, c)
      }
      setTimeout(() => {
        suppressFocusOpenRef.current = false
      }, 0)
    })
  }, [])

  const focusInputAtEnd = useCallback(
    (valueLen: number) => {
      focusInputWithCaret(valueLen, valueLen)
    },
    [focusInputWithCaret]
  )

  const handleSyntaxCustomTextRowClick = useCallback(() => {
    suppressFocusOpenRef.current = true
    setMenuOpen(false)
    setMenuView('syntax')
    focusInputAtEnd(patternValue.length)
  }, [patternValue.length, focusInputAtEnd])

  const applySuggestion = useCallback(
    (insert: string, suggestionLabel: string) => {
      const el = inputRef.current
      const start = el?.selectionStart ?? null
      const end = el?.selectionEnd ?? null
      const { value: newValue } = applyUrlPatternSuggestion(
        patternValue,
        insert,
        suggestionLabel,
        start,
        end
      )
      suppressFocusOpenRef.current = true
      setPatternValue(newValue)
      commitPattern(newValue)
      setMenuOpen(false)
      setMenuView('syntax')
      focusInputAtEnd(newValue.length)
    },
    [patternValue, commitPattern, focusInputAtEnd]
  )

  const handleApplyFullPattern = useCallback(
    (full: string) => {
      suppressFocusOpenRef.current = true
      setPatternValue(full)
      options.url_pattern = full
      setOptions(options)
      onNotifyChange()
      setMenuOpen(false)
      setMenuView('syntax')
      focusInputAtEnd(full.length)
    },
    [options, setOptions, onNotifyChange, focusInputAtEnd]
  )

  const onPatternChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setPatternValue(value)
    commitPattern(value)
    setSuggestionFilterActive(true)
    setMenuOpen(true)
  }

  const openMenu = useCallback(() => {
    if (disabled || suppressFocusOpenRef.current) return
    setMenuOpen((open) => {
      if (!open) {
        setSuggestionFilterActive(false)
      }
      return true
    })
  }, [disabled])

  const onPatternInputClick = useCallback(() => {
    if (disabled) return
    // Explicit click should always reopen the menu (suppress only blocks focus-driven open after a pick).
    suppressFocusOpenRef.current = false
    setMenuOpen((open) => {
      if (!open) {
        setSuggestionFilterActive(false)
      }
      return true
    })
    if (hasPlacedInitialCaretRef.current) {
      return
    }
    hasPlacedInitialCaretRef.current = true
    focusInputAtEnd(patternValue.length)
  }, [disabled, patternValue.length, focusInputAtEnd])

  const renderSyntaxMenu = () => (
    <div
      ref={menuRef}
      className="UrlPatternTextDropdown__menu"
      role="listbox"
      data-test-id="cs-ct-field-url-v2-syntax-menu"
    >
      {showSyntaxDropdownList && (
        <>
          <div className="UrlPatternTextDropdown__sectionLabel">{t('ContentTypeFieldProperties.UrlPatternTextDropdown.syntax_section') || 'Syntax'}</div>
          <ul className="Dropdown__menu__list">
            {visibleSyntaxMenuRows.map((row) => {
              switch (row.category) {
                case 'preset':
                  return (
                    <li key={row.id} className="Dropdown__menu__list__item" role="option">
                      <UrlPatternMenuRow
                        testId={getUrlPatternSyntaxMenuRowTestId(row)}
                        rowIcon={row.rowIcon}
                        title={row.displayName}
                        titleHelpText={t('ContentTypeFieldProperties.UrlPatternTextDropdown.preset_override_tooltip') || URL_PATTERN_PRESET_OVERRIDE_TOOLTIP}
                        subtitle={row.subtitle}
                        onClick={() => {
                          handleApplyFullPattern(row.insertToken)
                        }}
                      />
                    </li>
                  )
                case 'nested':
                  if (row.nestedRole === 'parent' && row.nestedSubmenu === 'field') {
                    return (
                      <li key={row.id} className="Dropdown__menu__list__item" role="option">
                        <UrlPatternMenuRow
                          testId={getUrlPatternSyntaxMenuRowTestId(row)}
                          rowIcon={row.rowIcon}
                          title={row.displayName}
                          subtitle={row.subtitle}
                          showChevron
                          onClick={() => setMenuView('fields')}
                        />
                      </li>
                    )
                  }
                  if (row.nestedRole === 'parent' && row.nestedSubmenu === 'taxonomy') {
                    return (
                      <li key={row.id} className="Dropdown__menu__list__item" role="option">
                        <UrlPatternMenuRow
                          testId={getUrlPatternSyntaxMenuRowTestId(row)}
                          rowIcon={row.rowIcon}
                          title={row.displayName}
                          subtitle={row.subtitle}
                          showChevron
                          onClick={() => setMenuView('taxonomy')}
                        />
                      </li>
                    )
                  }
                  return null
                case 'syntax':
                  return (
                    <li key={row.id} className="Dropdown__menu__list__item" role="option">
                      <UrlPatternMenuRow
                        testId={getUrlPatternSyntaxMenuRowTestId(row)}
                        rowIcon={row.rowIcon}
                        title={row.displayName}
                        subtitle={row.subtitle}
                        onClick={() => applySuggestion(row.insertToken, row.suggestionLabel)}
                      />
                    </li>
                  )
              }
            })}
            {shouldShowSyntaxCustomTextRow && (
              <li
                className="Dropdown__menu__list__item"
                role="option"
                aria-label={`Use typed fragment ${filterQ} as custom text`}
              >
                <UrlPatternMenuRow
                  testId="cs-ct-field-url-v2-syntax-custom-text"
                  rowIcon={null}
                  title={JSON.stringify(filterQ)}
                  titleClassName="UrlPatternTextDropdown__customTextQuoted"
                  subtitle={t('ContentTypeFieldProperties.UrlPatternTextDropdown.custom_text_subtitle') || URL_PATTERN_SYNTAX_CUSTOM_TEXT_SUBTITLE}
                  onClick={handleSyntaxCustomTextRowClick}
                />
              </li>
            )}
          </ul>
        </>
      )}
    </div>
  )

  const renderFieldsMenu = () => (
    <div
      // Same ref as syntax/taxonomy menus so document mousedown "outside" logic treats field rows as inside the panel.
      ref={menuRef}
      className="UrlPatternTextDropdown__menu"
      role="listbox"
      data-test-id="cs-ct-field-url-v2-field-menu"
    >
      <div className="UrlPatternTextDropdown__menuHeader">
        <Icon
          icon="LeftArrow"
          version="v2"
          size="medium"
          onClick={() => setMenuView('syntax')}
          aria-label="Back to syntax list"
          testId="cs-ct-field-url-v2-field-back-icon"
          style={{ cursor: 'pointer' }}
        />
        <span className="UrlPatternTextDropdown__menuHeaderTitle">{t('ContentTypeFieldProperties.UrlPatternTextDropdown.field_menu_title') || 'Field'}</span>
      </div>
      <div className="UrlPatternTextDropdown__infoBanner" data-test-id="cs-ct-field-url-v2-field-info">
        <Info content={URL_PATTERN_FIELD_SUBMENU_INFO} type="light" version="v2" dismissable={false} />
      </div>
      <ul className="Dropdown__menu__list">
        {visibleFieldSubmenuRows.length === 0 ? (
          <li className="Dropdown__menu__list__item Dropdown__menu__list__item--disable">{t('ContentTypeFieldProperties.UrlPatternTextDropdown.no_matching_text_fields') || 'No matching text fields'}</li>
        ) : (
          visibleFieldSubmenuRows.map((fieldRow) => (
            <li key={fieldRow.id} className="Dropdown__menu__list__item" role="option">
              <UrlPatternMenuRow
                testId={getUrlPatternFieldMenuRowTestId(fieldRow.schemaEntityUid ?? '')}
                rowIcon={fieldRow.rowIcon}
                title={fieldRow.displayName}
                subtitle={fieldRow.subtitle}
                onClick={() => applySuggestion(fieldRow.insertToken, fieldRow.suggestionLabel)}
              />
            </li>
          ))
        )}
      </ul>
    </div>
  )

  const renderTaxonomyMenu = () => (
    <div
      ref={menuRef}
      className="UrlPatternTextDropdown__menu"
      role="listbox"
      data-test-id="cs-ct-field-url-v2-taxonomy-menu"
    >
      <div className="UrlPatternTextDropdown__menuHeader">
        <Icon
          icon="LeftArrow"
          version="v2"
          size="medium"
          onClick={() => setMenuView('syntax')}
          aria-label="Back to syntax list"
          style={{ cursor: 'pointer' }}
          testId="cs-ct-field-url-v2-taxonomy-back-icon"
        />
        <span className="UrlPatternTextDropdown__menuHeaderTitle">{t('ContentTypeFieldProperties.UrlPatternTextDropdown.taxonomy_menu_title') || 'Taxonomy'}</span>
      </div>
      <ul className="Dropdown__menu__list">
        {taxonomyUidOptions.length === 0 ? (
          <li className="Dropdown__menu__list__item Dropdown__menu__list__item--disable">
            {t('ContentTypeFieldProperties.UrlPatternTextDropdown.no_taxonomy_uids') || 'No taxonomy UIDs on this content type'}
          </li>
        ) : visibleTaxonomySubmenuRows.length === 0 ? (
          <li className="Dropdown__menu__list__item Dropdown__menu__list__item--disable">{t('ContentTypeFieldProperties.UrlPatternTextDropdown.no_matching_taxonomies') || 'No matching taxonomies'}</li>
        ) : (
          visibleTaxonomySubmenuRows.map((taxRow) => (
            <li key={taxRow.id} className="Dropdown__menu__list__item" role="option">
              <UrlPatternMenuRow
                testId={getUrlPatternTaxonomyMenuRowTestId(taxRow.schemaEntityUid ?? '')}
                rowIcon={taxRow.rowIcon}
                title={taxRow.displayName}
                subtitle={taxRow.subtitle}
                onClick={() => applySuggestion(taxRow.insertToken, taxRow.suggestionLabel)}
              />
            </li>
          ))
        )}
      </ul>
    </div>
  )

  return (
    <div className="UrlPatternTextDropdown">
      <div className="FormFields__label-row" style={{ display: 'flex', marginBottom: 6 }}>
        <FieldLabel version="v2" testId="cs-ct-field-url-v2-pattern-label" htmlFor={`${uniqueKey}.urlPattern`}>
          {t('ContentTypeFieldProperties.UrlPatternTextDropdown.url_pattern_label') || 'URL Pattern'}
        </FieldLabel>
        <Help
          type="basic"
          testId="cs-ct-field-url-v2-pattern-help"
          text={t('ContentTypeFieldProperties.UrlPatternTextDropdown.url_pattern_help') || 'Select a predefined URL pattern. You can also select a field or taxonomy to insert its UID, or insert tokens directly. Start typing to filter options.'}
        />
      </div>

      <div className="UrlPatternTextDropdown__patternRow">
        <div className="UrlPatternTextDropdown__inputWrap">
          <TextInput
            inputRef={(el: HTMLInputElement | null) => {
              inputRef.current = el
            }}
            id={`${uniqueKey}.urlPattern`}
            name={`${uniqueKey}.urlPattern`}
            version="v2"
            width="full"
            placeholder={t('ContentTypeFieldProperties.UrlPatternTextDropdown.url_pattern_placeholder') || '/:blog/:category/:title'}
            disabled={disabled}
            value={patternValue || ''}
            onChange={onPatternChange}
            onClick={onPatternInputClick}
            autoComplete="off"
            testId={getContentTypeFieldDataTestId(schema, 'basic', CONTENT_TYPE_FIELD_ACTION_IDS.CUSTOM_URL_INPUT)}
          />
          {menuOpen && !disabled && menuView === 'syntax' && renderSyntaxMenu()}
          {menuOpen && !disabled && menuView === 'fields' && renderFieldsMenu()}
          {menuOpen && !disabled && menuView === 'taxonomy' && renderTaxonomyMenu()}
          {!menuOpen && (
            <Paragraph
              tagName="p"
              variant="p2"
              variantStyle="regular"
              className="UrlPatternTextDropdown__patternHint"
              testId="cs-ct-field-url-v2-pattern-hint"
              text={t('ContentTypeFieldProperties.UrlPatternTextDropdown.url_pattern_hint') || URL_PATTERN_HINT_TEXT}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default UrlPatternTextDropdown
